import type { ReactElement } from 'react';
import {
    CardDetailsModel,
    defaultDemoIpgConfigByKey,
    defaultIpgConfigByKey,
    resolveDefaultCardDetailsModel,
    type MerchantConfig,
} from '@pillar-portal/api-types';
import {
    type ValorIpgConfig,
    type ValorIpgConfigCustomization,
} from '@pillar-portal/api-types/ipg/valor/backend-config';
import {
    ValorIpgConfigurationMode,
    ValorIpgSystemId,
    ValorIpgSystemIdKey,
} from '@pillar-portal/api-types/ipg/valor/system-constants';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { IpgConfigEditorAdapter } from '../../ipg-config-editor-adapter';
import { CardDetailsModelControl } from '../CardDetailsModelControl';

export type ValorOrganizationIpgPortalMode = ValorIpgConfigurationMode;

export interface ValorOrganizationIpgConfigEditorState {
    readonly ipgSystemId: typeof ValorIpgSystemId;
    readonly ipgSystemIdKey: typeof ValorIpgSystemIdKey;
    readonly portalMode: ValorOrganizationIpgPortalMode;
    readonly portalUrl: string;
    readonly defaultApiUrl: string;
    readonly defaultChannel: string;
    readonly cardDetailsModel: CardDetailsModel;
}

export interface ValorOrganizationIpgConfigEditorProps {
    readonly editorState: ValorOrganizationIpgConfigEditorState;
    readonly onChange: (nextState: ValorOrganizationIpgConfigEditorState) => void;
    readonly productionPresetAvailable: boolean;
}

export interface ValorOrganizationIpgReadOnlySummaryProps {
    readonly editorState: ValorOrganizationIpgConfigEditorState;
}

export const ValorOrganizationIpgSystemIdKey: typeof ValorIpgSystemIdKey = ValorIpgSystemIdKey;

export const ValorOrganizationIpgSystemId: typeof ValorIpgSystemId = ValorIpgSystemId;

export function cloneValorOrganizationIpgConfig(config: ValorIpgConfig): ValorIpgConfig {
    return {
        ...config,
        allowedWebsites: config.allowedWebsites.map((allowedWebsite) => ({
            ...allowedWebsite,
            allowedUrlPatterns: [...allowedWebsite.allowedUrlPatterns],
            urlPatternsRules: allowedWebsite.urlPatternsRules.map((fetchPolicy) => ({
                action: fetchPolicy.action,
                urlPatterns: [...fetchPolicy.urlPatterns],
            })),
            iframeUrlPatterns: allowedWebsite.iframeUrlPatterns.map((iframePolicy) => ({
                action: iframePolicy.action,
                urlPatterns: [...iframePolicy.urlPatterns],
            })),
        })),
        rpaModules: [...config.rpaModules],
    };
}

export function getValorOrganizationIpgPortalUrl(config: ValorIpgConfig): string {
    return config.allowedWebsites[0]?.url ?? '';
}

export function getValorOrganizationIpgDefaultApiUrl(config: ValorIpgConfig): string {
    return config.default_api_url ?? '';
}

export function getProductionValorOrganizationIpgConfig(): ValorIpgConfig {
    const config = defaultIpgConfigByKey[ValorOrganizationIpgSystemIdKey];
    if (config.ipg_id !== ValorOrganizationIpgSystemId) {
        throw new Error('Invalid Valor production IPG configuration.');
    }
    return config;
}

export function getDemoValorOrganizationIpgConfig(): ValorIpgConfig {
    const demoIpgConfig = defaultDemoIpgConfigByKey[ValorOrganizationIpgSystemIdKey];
    if (demoIpgConfig === undefined || demoIpgConfig.ipg_id !== ValorOrganizationIpgSystemId) {
        throw new Error('Missing demo IPG configuration.');
    }
    return demoIpgConfig;
}

export function getDefaultValorOrganizationIpgConfig(): ValorIpgConfig {
    return cloneValorOrganizationIpgConfig(getDemoValorOrganizationIpgConfig());
}

export function getEffectiveValorOrganizationIpgConfig(
    merchantConfig: MerchantConfig | undefined
): ValorIpgConfig {
    const persistedManagedIpgConfig = merchantConfig?.ipg[ValorOrganizationIpgSystemIdKey];
    if (persistedManagedIpgConfig?.ipg_id === ValorOrganizationIpgSystemId) {
        return cloneValorOrganizationIpgConfig(persistedManagedIpgConfig);
    }
    return getDefaultValorOrganizationIpgConfig();
}

export function deriveValorOrganizationIpgPortalModeFromConfig(
    merchantConfig: MerchantConfig | undefined
): ValorOrganizationIpgPortalMode {
    return getEffectiveValorOrganizationIpgConfig(merchantConfig).configuration_mode;
}

export function buildValorOrganizationIpgConfigEditorState(
    merchantConfig: MerchantConfig | undefined
): ValorOrganizationIpgConfigEditorState {
    const effectiveManagedIpgConfig = getEffectiveValorOrganizationIpgConfig(merchantConfig);
    return {
        ipgSystemId: ValorOrganizationIpgSystemId,
        ipgSystemIdKey: ValorOrganizationIpgSystemIdKey,
        portalMode: deriveValorOrganizationIpgPortalModeFromConfig(merchantConfig),
        portalUrl: getValorOrganizationIpgPortalUrl(effectiveManagedIpgConfig),
        defaultApiUrl: getValorOrganizationIpgDefaultApiUrl(effectiveManagedIpgConfig),
        defaultChannel: effectiveManagedIpgConfig.default_channel ?? '',
        cardDetailsModel:
            effectiveManagedIpgConfig.card_details_model ??
            resolveDefaultCardDetailsModel(ValorOrganizationIpgSystemId),
    };
}

export function formatValorOrganizationIpgPortalModeLabel(
    portalMode: ValorOrganizationIpgPortalMode
): string {
    if (portalMode === 'PRODUCTION') {
        return 'Production';
    }
    if (portalMode === 'DEMO') {
        return 'Demo';
    }
    return 'Custom';
}

export function getValorOrganizationIpgConfigForPortalMode(
    portalMode: ValorOrganizationIpgPortalMode
): ValorIpgConfig | null {
    if (portalMode === 'PRODUCTION') {
        return getProductionValorOrganizationIpgConfig();
    }
    if (portalMode === 'DEMO') {
        return getDemoValorOrganizationIpgConfig();
    }
    return null;
}

function isValidUrl(value: string): boolean {
    try {
        new URL(value);
        return true;
    } catch {
        return false;
    }
}

export function buildValorOrganizationIpgConfigForSaveOrThrow(
    editorState: ValorOrganizationIpgConfigEditorState
): ValorIpgConfigCustomization {
    const managedIpgConfig = getValorOrganizationIpgConfigForPortalMode(editorState.portalMode);
    if (managedIpgConfig !== null) {
        return {
            ipg_id: ValorIpgSystemId,
            configuration_mode: editorState.portalMode,
            card_details_model: editorState.cardDetailsModel,
        };
    }

    const trimmedPortalUrl = editorState.portalUrl.trim();
    const trimmedDefaultApiUrl = editorState.defaultApiUrl.trim();
    const trimmedDefaultChannel = editorState.defaultChannel.trim();

    if (!isValidUrl(trimmedPortalUrl)) {
        throw new Error('Payment gateway portal URL must be a valid URL');
    }
    if (!isValidUrl(trimmedDefaultApiUrl)) {
        throw new Error('Payment gateway default API URL must be a valid URL');
    }

    const portalOrigin = new URL(trimmedPortalUrl).origin;
    const productionManagedIpgConfig = cloneValorOrganizationIpgConfig(
        getProductionValorOrganizationIpgConfig()
    );
    const productionAllowedWebsite = productionManagedIpgConfig.allowedWebsites[0];
    if (productionAllowedWebsite === undefined) {
        throw new Error('Managed payment gateway production config is missing an allowed website');
    }

    return {
        ipg_id: ValorIpgSystemId,
        configuration_mode: ValorIpgConfigurationMode.CUSTOM,
        portalUrl: trimmedPortalUrl,
        urlPattern: `${portalOrigin}/*`,
        allowedWebsites: [
            {
                allowedUrlPatterns: [`${portalOrigin}/*`],
                sessionPersistence: productionAllowedWebsite.sessionPersistence,
                promoteSessionCookies: productionAllowedWebsite.promoteSessionCookies,
                urlPatternsRules: productionAllowedWebsite.urlPatternsRules,
                fetchDefaultAction: productionAllowedWebsite.fetchDefaultAction,
                iframeUrlPatterns: productionAllowedWebsite.iframeUrlPatterns,
                iframeDefaultAction: productionAllowedWebsite.iframeDefaultAction,
            },
        ],
        default_api_url: trimmedDefaultApiUrl,
        default_channel: trimmedDefaultChannel.length > 0 ? trimmedDefaultChannel : undefined,
        card_details_model: editorState.cardDetailsModel,
    };
}

function ValorOrganizationIpgPortalModeRadio(props: {
    readonly label: string;
    readonly checked: boolean;
    readonly onChange: () => void;
    readonly disabled: boolean;
}): ReactElement {
    return (
        <div className="flex items-center gap-2 text-sm">
            <span
                className="-m-[3px] inline-flex p-[3px]"
                onClick={(event) => {
                    if (props.disabled) {
                        return;
                    }
                    if (event.target !== event.currentTarget) {
                        return;
                    }
                    props.onChange();
                }}
            >
                <input
                    type="radio"
                    aria-label={props.label}
                    name="managed_ipg_portal_mode"
                    checked={props.checked}
                    disabled={props.disabled}
                    title={
                        props.disabled
                            ? 'Production presets are available only in production Portal environments.'
                            : undefined
                    }
                    className="h-4 w-4 accent-blue-600 disabled:opacity-50"
                    onChange={props.onChange}
                />
            </span>
            <span>{props.label}</span>
        </div>
    );
}

export function ValorOrganizationIpgConfigEditor({
    editorState,
    onChange,
    productionPresetAvailable,
}: ValorOrganizationIpgConfigEditorProps): ReactElement {
    return (
        <div className="space-y-3 rounded-md border p-3">
            <p className="text-sm font-medium">Gateway defaults</p>
            <CardDetailsModelControl
                ipgSystemId={editorState.ipgSystemId}
                value={editorState.cardDetailsModel}
                onChange={(cardDetailsModel) => onChange({ ...editorState, cardDetailsModel })}
            />
            <div className="space-y-2">
                <p className="text-sm font-medium">Portal mode</p>
                <div className="flex flex-wrap gap-4">
                    {(['PRODUCTION', 'DEMO', 'CUSTOM'] as const).map((portalMode) => (
                        <ValorOrganizationIpgPortalModeRadio
                            key={portalMode}
                            label={formatValorOrganizationIpgPortalModeLabel(portalMode)}
                            checked={editorState.portalMode === portalMode}
                            disabled={portalMode === 'PRODUCTION' && !productionPresetAvailable}
                            onChange={() => {
                                const managedIpgConfig =
                                    getValorOrganizationIpgConfigForPortalMode(portalMode);
                                if (managedIpgConfig !== null) {
                                    onChange({
                                        ...editorState,
                                        portalMode,
                                        portalUrl:
                                            getValorOrganizationIpgPortalUrl(managedIpgConfig),
                                        defaultApiUrl:
                                            getValorOrganizationIpgDefaultApiUrl(managedIpgConfig),
                                        defaultChannel: managedIpgConfig.default_channel ?? '',
                                    });
                                    return;
                                }
                                onChange({
                                    ...editorState,
                                    portalMode,
                                });
                            }}
                        />
                    ))}
                </div>
            </div>
            {editorState.portalMode === 'CUSTOM' && (
                <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                        <Label htmlFor="managed_ipg_portal_url">Gateway portal URL</Label>
                        <Input
                            id="managed_ipg_portal_url"
                            value={editorState.portalUrl}
                            onChange={(event) => {
                                onChange({
                                    ...editorState,
                                    portalUrl: event.target.value,
                                });
                            }}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="managed_ipg_default_api_url">Gateway default API URL</Label>
                        <Input
                            id="managed_ipg_default_api_url"
                            value={editorState.defaultApiUrl}
                            onChange={(event) => {
                                onChange({
                                    ...editorState,
                                    defaultApiUrl: event.target.value,
                                });
                            }}
                        />
                    </div>
                </div>
            )}
            {editorState.portalMode === 'CUSTOM' && (
                <div className="space-y-2">
                    <Label htmlFor="managed_ipg_default_channel">Gateway default channel</Label>
                    <Input
                        id="managed_ipg_default_channel"
                        value={editorState.defaultChannel}
                        onChange={(event) => {
                            onChange({
                                ...editorState,
                                defaultChannel: event.target.value,
                            });
                        }}
                    />
                </div>
            )}
            {editorState.portalMode !== 'CUSTOM' && (
                <p className="text-sm">Portal URL: {editorState.portalUrl}</p>
            )}
        </div>
    );
}

export function ValorOrganizationIpgReadOnlySummary({
    editorState,
}: ValorOrganizationIpgReadOnlySummaryProps): ReactElement {
    return (
        <div className="space-y-2">
            <p className="text-sm font-medium">Gateway configuration</p>
            <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
                <p>
                    Portal mode: {formatValorOrganizationIpgPortalModeLabel(editorState.portalMode)}
                </p>
                <p>
                    Gateway default channel:{' '}
                    {editorState.defaultChannel.trim().length > 0
                        ? editorState.defaultChannel
                        : 'Not configured'}
                </p>
                {editorState.portalMode === 'CUSTOM' && (
                    <>
                        <p>Gateway portal URL: {editorState.portalUrl}</p>
                        <p>Gateway default API URL: {editorState.defaultApiUrl}</p>
                    </>
                )}
            </div>
        </div>
    );
}

export const ValorOrganizationIpgConfigEditorAdapter: IpgConfigEditorAdapter<
    ValorOrganizationIpgConfigEditorState,
    ValorIpgConfigCustomization
> = {
    ipgSystemId: ValorOrganizationIpgSystemId,
    ipgSystemIdKey: ValorOrganizationIpgSystemIdKey,
    label: 'Valor',
    capabilities: {
        supportsEditableConfig: true,
        supportsReadOnlySummary: true,
        supportsDefaultCredentialPlaceholders: true,
    },
    createEditableState: ({ merchantConfig }) =>
        buildValorOrganizationIpgConfigEditorState(merchantConfig),
    validateEditableState: ({ editorState }) => {
        buildValorOrganizationIpgConfigForSaveOrThrow(editorState);
        return [];
    },
    buildSavePayload: ({ editorState }) =>
        buildValorOrganizationIpgConfigForSaveOrThrow(editorState),
    renderEditable: ({ editorState, onChange, productionPresetAvailable }) => (
        <ValorOrganizationIpgConfigEditor
            editorState={editorState}
            onChange={onChange}
            productionPresetAvailable={productionPresetAvailable}
        />
    ),
    renderReadOnlySummary: ({ merchantConfig }) => (
        <ValorOrganizationIpgReadOnlySummary
            editorState={buildValorOrganizationIpgConfigEditorState(merchantConfig)}
        />
    ),
};
